import {
  ActionIcon,
  Anchor,
  Badge,
  Group,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { Box, Expand, SquarePen, SquarePlay } from "lucide-react";
import { useState } from "react";

import { useGetCatalog } from "../../api/generated/endpoints/generation/generation";
import type { GenerationOutput, Job } from "../../api/generated/model";
import { formatDateTime } from "../../ui/format";
import { MediaCard, MediaExpired, MediaFailed, MediaLoading, MediaPlaceholder } from "../../ui/MediaCard";
import { MediaGrid } from "../../ui/MediaGrid";
import { UsageNotes } from "../../ui/UsageNotes";
import classes from "./GenerationRow.module.css";
import { typeLabel } from "../../platform/studio/draft";
import { useRowActions } from "../../platform/jobs/GenerationActions";
import { modelTitle } from "../../platform/jobs/model-name";
import { outputKey, shownOutputs } from "../../platform/jobs/output-picks";
import { generationStatus, type Generation } from "./generations";
import { statusLook } from "../../platform/jobs/detail/job-status";
import { previewKind } from "../../platform/media/output-kind";
import { OutputMedia } from "../../platform/media/OutputMedia";
import { outputAlt, outputMedium } from "../../platform/jobs/detail/outputs";
import { failedWords } from "../../platform/jobs/failed-words";
import { expiredLine, isExpired } from "../../platform/jobs/expired";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { PROMPT_SAVED, useSavePrompt } from "../../platform/media/save-prompt";
import { ImageCardActions } from "./ImageCardActions";
import { previewId } from "./GenerationPreview";
import { OpenLayerEditor } from "./layer-editor/OpenLayerEditor";

/** Live's note on a talking head's row (`my-generation/generation/header`, `TALKING_HEAD_USAGE_NOTES`). */
export const TALKING_HEAD_NOTES = ["Average generation time 10 min"];
/** Live's talking heads, simple and Advanced (`TALKING_HEAD_GENERATION_TYPES`). */
const TALKING_HEADS: readonly string[] = ["video.talking_head", "video.talking_head_advanced"];

export interface GenerationRowProps {
  teamId: string;
  /** The studio's brand, whose library and projects the row's saves go to. */
  brandId: string;
  /** The row's jobs: one, or every actor's of one talking head (`groupGenerations`). */
  jobs: Generation["jobs"];
  /** Reads the list again, for fresh URLs after a preview fails to load (G5). */
  readAgain: () => void;
  /** Opens a job's detail over the page: "Open" the first's. */
  onOpen: (jobId: string) => void;
  /** Opens the lightbox on a delivered output (`previewId`): an image's click, or "Open preview". */
  onPreview: (id: string) => void;
}

/** A card of the row: an output of a job, or why a job failed. */
type Card = { job: Job; output: GenerationOutput } | { job: Job; error: NonNullable<Job["error"]> };

/**
 * A card's alt: the prompt's, or, on a talking head's row, which video of the row it is, as its
 * jobs keep no prompt and each has only an output 1.
 */
function cardAlt(jobs: Generation["jobs"], job: Job, output: GenerationOutput, place: number) {
  return jobs.length === 1 ? outputAlt(job, output) : `Video ${place + 1} of this generation`;
}

/**
 * One generation in the studio's My Generations (design-system.md 3.3): a job, or a talking
 * head's jobs, one per actor, as live's one generation. A header with what it is, when, its
 * status and settings, and live's row actions on the right (save, download, save to project,
 * delete), then its outputs as media cards, a soft box for each one still being made, or a box
 * saying why a job failed in that job's place. Its detail holds the rest.
 */
export function GenerationRow({ teamId, brandId, jobs, readAgain, onOpen, onPreview }: GenerationRowProps) {
  const [job] = jobs;
  // Live's layer editor, open over the page on this Layerize.
  const [editing, setEditing] = useState(false);
  const actions = useRowActions({ teamId, brandId, jobs, readAgain });
  const prompt = useSavePrompt(teamId, brandId);
  const status = statusLook(generationStatus(jobs));
  const ratio = job.params?.aspect_ratio ?? null;
  const count = jobs.reduce((sum, each) => sum + shownOutputs(each).length, 0);
  const when = formatDateTime(job.created_at);
  // Live's header: the type's badge, the model's by its title (a box icon), then the date.
  const model = modelTitle(useGetCatalog().data, job);
  // Each job's outputs in its place, or, for a job that failed, one card saying why.
  const cards = jobs.flatMap((each): Card[] =>
    each.error === null
      ? shownOutputs(each).map((output) => ({ job: each, output }))
      : [{ job: each, error: each.error }],
  );
  return (
    <article className={classes.row} aria-label={`${typeLabel(job.type)}, ${when}`}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb={8}>
        {/* On a narrow screen the facts wrap onto a second line rather than cut the date or the
            status short (worker 9's sweep, spec ui-switch-classic-simple-pro decision 21). */}
        <Group gap={8} wrap="wrap" miw={0} className={classes.facts}>
          <span className={classes.fact}>{typeLabel(job.type)}</span>
          {model === null ? null : (
            <span className={`${classes.fact} ${classes.model}`}>
              <Box size={14} strokeWidth={1.5} aria-hidden />
              {model}
            </span>
          )}
          <Text size="xs" c="var(--cai-color-text-muted)" className={classes.when}>
            {when}
          </Text>
          <UsageNotes notes={TALKING_HEADS.includes(job.type) ? TALKING_HEAD_NOTES : []} />
          {ratio === null ? null : <span className={classes.fact}>{ratio}</span>}
          {count === 0 ? null : <span className={classes.fact}>{count}</span>}
          <Badge size="sm" variant="light" color={status.color} className={classes.status}>
            {status.label}
          </Badge>
        </Group>
        <Group gap={8} wrap="nowrap">
          {/* Live's "Edit layers" on a finished Layerize (spec G13b). */}
          {job.type === "image.layerize" && job.status === "succeeded" ? (
            <Tooltip label="Edit layers">
              <ActionIcon variant="subtle" aria-label="Edit layers" onClick={() => setEditing(true)}>
                <SquarePen size={16} />
              </ActionIcon>
            </Tooltip>
          ) : null}
          {actions.icons}
          <Anchor component="button" type="button" size="sm" onClick={() => onOpen(job.id)}>
            Open
          </Anchor>
        </Group>
      </Group>
      {actions.notes}
      {/* Live's Save Prompt toast, said under the header for its 5 s; a refusal shows below (issue 908). */}
      {prompt.said === null ? null : (
        <Text component="output" size="sm">
          {PROMPT_SAVED}
        </Text>
      )}
      {prompt.error === null ? null : <ErrorAlert error={prompt.error} />}
      <MediaGrid>
        {cards.map((card, at) => {
          if ("error" in card) {
            const words = failedWords(card.job.type, card.error.message);
            return <MediaFailed key={card.job.id} {...words} medium={outputMedium(card.job)} />;
          }
          const { output } = card;
          const key = outputKey({ jobId: card.job.id, index: output.index });
          // A job's own outputs by their index; a talking head's by their place in the row.
          const place = jobs.length === 1 ? output.index : at;
          const kind = previewKind(output);
          // Kept in the library, by the server's read or by a save made here (G12).
          const saved = actions.saved.has(key) || (output.saved_asset_id ?? null) !== null;
          // Past the bucket's days and never saved: one line, no file and no action.
          if (isExpired(output)) {
            return <MediaExpired key={key} title={expiredLine(card.job)} medium={outputMedium(card.job)} />;
          }
          if (output.status === "delivered" && output.url !== null && kind !== null) {
            const media = (
              <OutputMedia
                kind={kind}
                src={output.url}
                thumb={output.thumb_url}
                poster={output.poster_url}
                alt={cardAlt(jobs, card.job, output, place)}
                onExpired={readAgain}
              />
            );
            const pick = { jobId: card.job.id, index: output.index };
            const menu =
              kind === "image" ? (
                <ImageCardActions
                  teamId={teamId}
                  brandId={brandId}
                  job={card.job}
                  output={output}
                  number={place + 1}
                  saved={saved}
                  saving={actions.saving}
                  onSave={() => actions.saveOne(pick)}
                  onSaveToProject={() => actions.saveToProject(pick)}
                  onDownload={() => actions.downloadOne(card.job, output)}
                  onEditLayers={() => setEditing(true)}
                  prompt={prompt}
                />
              ) : undefined;
            // An image opens the lightbox (Daniil's); a video keeps its own controls, and both
            // open it from "Open preview" in the top right corner. A video also opens in the
            // editor from left of it (beyond live, Daniil's; spec open-from-studio).
            const preview = () => onPreview(previewId(card.job, output));
            const label = kind === "image" ? "image" : "video";
            const corner = (
              <Group gap={6} wrap="nowrap">
                {kind === "video" ? (
                  <Tooltip label="Open in the editor">
                    <ActionIcon
                      variant="text-white"
                      className={classes.expand}
                      aria-label={`Open video ${place + 1} in the editor`}
                      onClick={() => actions.openInEditor(card.job, output)}
                    >
                      <SquarePlay size={16} aria-hidden />
                    </ActionIcon>
                  </Tooltip>
                ) : null}
                <Tooltip label="Open preview">
                  <ActionIcon
                    variant="text-white"
                    className={classes.expand}
                    aria-label={`Open preview of ${label} ${place + 1}`}
                    onClick={preview}
                  >
                    <Expand size={16} aria-hidden />
                  </ActionIcon>
                </Tooltip>
              </Group>
            );
            return (
              <MediaCard key={key} saved={saved} menu={menu} corner={corner}>
                {kind === "image" ? (
                  <UnstyledButton className={classes.open} onClick={preview}>
                    {media}
                  </UnstyledButton>
                ) : (
                  media
                )}
              </MediaCard>
            );
          }
          // A failed output keeps the still box; one being made shows live's loader.
          if (output.status === "failed") {
            return (
              <MediaPlaceholder
                key={key}
                aspectRatio={ratio}
                label={`Output ${place + 1}, failed`}
                medium={outputMedium(card.job)}
              />
            );
          }
          return (
            <MediaLoading
              key={key}
              aspectRatio={ratio}
              label={`Output ${place + 1}, waiting`}
              medium={outputMedium(card.job)}
            />
          );
        })}
      </MediaGrid>
      {editing ? (
        <OpenLayerEditor teamId={teamId} brandId={brandId} jobId={job.id} onClose={() => setEditing(false)} />
      ) : null}
    </article>
  );
}
